import React from "react";
import { Link } from "react-router-dom";
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag, Lock } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { createOrder } from "../lib/api";
import { toast } from "sonner";

const CartPage = () => {
  const { items, updateQty, removeItem, subtotal, clear } = useCart();

  const checkout = async () => {
    try {
      const order = await createOrder(items);
      toast.success(`Order ${order.id.slice(0, 8)} confirmed. Thank you!`);
      clear();
    } catch {
      toast.error("Could not place order. Try again.");
    }
  };

  return (
    <main className="bg-[#FFF2EC] min-h-[60vh]">
      <div className="max-w-6xl mx-auto px-4 py-12 md:py-20">
        <div className="text-[11px] uppercase tracking-[0.35em] text-[#DD4D2A] font-semibold mb-3">
          Your Bag
        </div>
        <h1 className="font-serif-display text-4xl md:text-6xl font-semibold">
          Shopping <span className="italic">Cart</span>
        </h1>

        {items.length === 0 ? (
          <div className="mt-12 bg-white rounded-3xl border border-[#1A1410]/10 p-12 text-center">
            <ShoppingBag className="w-12 h-12 text-[#1A1410]/30 mx-auto" />
            <h2 className="mt-4 font-serif-display text-2xl font-semibold">
              Your cart is empty
            </h2>
            <p className="mt-2 text-[#1A1410]/60">
              Discover our bestselling rebounders and add your favorite.
            </p>
            <Link
              to="/collections"
              className="btn-coral inline-flex items-center gap-2 mt-6 px-7 py-3.5 rounded-full text-sm uppercase"
            >
              Continue Shopping
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-10 grid lg:grid-cols-[1fr_380px] gap-8">
            <div className="space-y-4">
              {items.map((i) => (
                <div
                  key={i.id}
                  className="flex gap-4 bg-white rounded-2xl p-4 border border-[#1A1410]/10"
                >
                  <img
                    src={i.image}
                    alt={i.name}
                    className="w-28 h-28 object-cover rounded-xl"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-display text-lg font-semibold line-clamp-2">
                      {i.name}
                    </div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="font-semibold">${i.price}.00</span>
                      {i.compareAt && (
                        <span className="text-sm text-[#1A1410]/45 line-through">
                          ${i.compareAt}.00
                        </span>
                      )}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center border border-[#1A1410]/15 rounded-full bg-white">
                        <button
                          onClick={() => updateQty(i.id, i.qty - 1)}
                          className="p-2 hover:bg-[#1A1410]/5 rounded-l-full"
                          aria-label="Decrease"
                        >
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="px-4 text-sm font-medium">{i.qty}</span>
                        <button
                          onClick={() => updateQty(i.id, i.qty + 1)}
                          className="p-2 hover:bg-[#1A1410]/5 rounded-r-full"
                          aria-label="Increase"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(i.id)}
                        className="text-[#1A1410]/55 hover:text-[#DD4D2A] text-sm inline-flex items-center gap-1.5"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <aside className="bg-white rounded-3xl border border-[#1A1410]/10 p-6 h-fit sticky top-28">
              <h3 className="font-serif-display text-xl font-semibold">
                Order Summary
              </h3>
              <div className="mt-5 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#1A1410]/65">Subtotal</span>
                  <span className="font-semibold">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#1A1410]/65">Shipping</span>
                  <span className="font-semibold text-[#DD4D2A]">Free</span>
                </div>
                <div className="border-t border-[#1A1410]/10 pt-3 flex justify-between items-baseline">
                  <span className="text-[#1A1410]/65">Total</span>
                  <span className="font-serif-display text-2xl font-semibold">
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
              </div>
              <button
                onClick={checkout}
                className="mt-6 w-full btn-coral py-4 rounded-full text-sm uppercase inline-flex items-center justify-center gap-2"
              >
                <Lock className="w-4 h-4" />
                Secure Checkout
              </button>
              <div className="mt-3 flex items-center justify-center gap-2 text-[10px] uppercase tracking-wider text-[#1A1410]/55">
                <Lock className="w-3 h-3" />
                256-bit SSL encrypted checkout
              </div>
              <div className="mt-3 flex items-center justify-center gap-1.5">
                {["VISA", "MC", "AMEX", "PayPal", "Apple Pay"].map((p) => (
                  <span
                    key={p}
                    className="text-[9px] font-bold px-1.5 py-0.5 bg-[#1A1410]/5 rounded"
                  >
                    {p}
                  </span>
                ))}
              </div>
              <Link
                to="/collections"
                className="mt-3 block text-center text-xs uppercase tracking-wider text-[#1A1410]/60 hover:text-[#DD4D2A]"
              >
                Continue Shopping
              </Link>
            </aside>
          </div>
        )}
      </div>
    </main>
  );
};

export default CartPage;
